import { useCallback, useEffect, useRef } from 'react';
import { useBridge } from './useBridge';

/**
 * Waits for the browser to actually paint before running `cb`. The double
 * `requestAnimationFrame` lets a React commit flush to the screen first, so the
 * host never drops its loading overlay on a frame that hasn't painted yet.
 */
const afterPaint = (cb: () => void): void => {
  requestAnimationFrame(() => {
    requestAnimationFrame(cb);
  });
};

/**
 * Returns a stable, idempotent `signalReady()` that tells the host the flow
 * has first-paintable content (emits `flow:ready`). Repeat calls are no-ops.
 *
 * Use this in `manual` readiness flows — flows that load data on entry and
 * must not reveal their internal loader. Call `signalReady()` once real
 * content is about to paint; the emit is deferred until after the next paint
 * so the host overlay is only dropped once that content is actually on screen
 * (otherwise the overlay lifts a frame early and the loading frame flashes
 * through). `auto` flows (the default) get this via `useAutoFlowReadyOnPaint`.
 */
export const useFlowReady = (): { signalReady: () => void } => {
  const bridge = useBridge();
  const emittedRef = useRef(false);

  const signalReady = useCallback(() => {
    if (emittedRef.current) return;
    if (!bridge.flowId) return;
    emittedRef.current = true;
    const { flowId } = bridge;
    afterPaint(() => bridge.emit({ type: 'flow:ready', flowId }));
  }, [bridge]);

  return { signalReady };
};

/**
 * Emits `flow:ready` automatically after the first paint when `enabled`.
 * Used by `FlowLoader` for `auto` flows (the default). The paint wait lives in
 * `signalReady`, so this just calls it once the flow mounts. No-op when
 * `enabled` is false (the flow owns its own signal).
 */
export const useAutoFlowReadyOnPaint = (enabled: boolean): void => {
  const { signalReady } = useFlowReady();

  useEffect(() => {
    if (!enabled) return;
    signalReady();
  }, [enabled, signalReady]);
};
